import { differenceInHours } from 'date-fns'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'

import FollowArtist from 'components/follow/artist'
import NumberOfViews from 'components/stars/NumberOfViews'
import AuctionsBanner from 'components/stars/auctions-banner'
import {
  GetAuctionDataQuery,
  GetEventsQuery,
  GetEventsQueryVariables,
  GetStarBySlugQuery,
  StarAuctionsQuery,
} from 'gql'
import { useGeolocation, useParseJSON, useQueryParams } from '~hooks'
import { addApolloState, initializeApollo } from '~services/apollo'
import { EventsList, MetaTags, EventsDateRangeFilter } from '~shared'
import Sidebar from '~shared/Side'
import { getEventsDateRangeFilters, getServerTimeInApiFormat } from '~utils/graphql'
import { Desktop, Grid, Heading, Layout, Mobile, NeonBox, Typography } from '~ui'
import { getServerDate } from '~utils/time-new'
import { STAR } from 'routes/paths'
import { DateRangeFilterValues } from 'constants/event-filters'
import NearEventsList from 'components/stars/NearEventsList'
import { ArtistMeta } from 'types/artist'
import { useCallback } from 'react'
import checkEventIsAvailableInVenue from '~utils/checkEventIsAvailableInVenue'
import { ALL_STARS_SLUG, GET_STAR_BY_SLUG } from 'gql/stars/queries'
import { GET_EVENTS, STAR_AUCTIONS } from 'gql/artwork/queries'
import { PARTNER_SLUG } from 'constants/constant'

const PRIVATE_STATUS_LIST = ['private', 'draft', 'deleted']

export const getStaticPaths: GetStaticPaths = async () => {
  const apollo = initializeApollo()
  const { data } = await apollo.query({
    query: ALL_STARS_SLUG,
    variables: { status: 'public' },
  })
  const paths = data.allArtists.edges.map(({ node }) => ({ params: { slug: node.slug } }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const apollo = initializeApollo()

  const { data: artist, error } = await apollo.query<GetStarBySlugQuery>({
    query: GET_STAR_BY_SLUG,
    variables: { slug: params.slug },
  })

  const { data: auctions, error: auctionError } = await apollo.query<StarAuctionsQuery>({
    query: STAR_AUCTIONS,
    variables: {
      artistsSlug: params.slug,
      salesAs: 'auction',
      status: 'public',
      isActive: true,
      partnerSlug: PARTNER_SLUG,
    },
  })

  const isPrivate = PRIVATE_STATUS_LIST.includes(artist.allArtists.edges[0]?.node.status)

  const eventStartDateGte = getServerTimeInApiFormat(true)
  const datesRange = getEventsDateRangeFilters()

  const { data: events, error: eventsError } = await apollo.query<
    GetEventsQuery,
    GetEventsQueryVariables
  >({
    query: GET_EVENTS,
    variables: {
      starSlug: Array.isArray(params.slug) ? params.slug[0] : params.slug,
      orderBy: 'event_start_date_local',
      salesAs: 'buy_it_now_option',
      featuredDateIsNull: false,
      ...datesRange,
      eventStartDateGte: isPrivate ? null : eventStartDateGte,
      announceDateGte: isPrivate ? eventStartDateGte : null,
      statusIn: isPrivate ? 'private,public' : 'public',
      originIn: 'granted',
      first: 10,
    },
  })

  return addApolloState(apollo, {
    notFound: error || auctionError || eventsError || !artist.allArtists.edges.length,
    props: {
      artist,
      events,
      auctions,
      isPrivate,
      datesRange,
      eventStartDateGte,
    },
    revalidate: 60, // in secs
  })
}

const StarPage: NextPage<{
  artist: GetStarBySlugQuery
  auctions: GetAuctionDataQuery
  events: GetEventsQuery
  datesRange: {
    eventStartDateGte: string
    eventStartDateLte: string
  }
  isPrivate
  eventStartDateGte: string
}> = ({ artist, auctions, events, datesRange, eventStartDateGte }) => {
  const {
    query: { slug, ...query },
  } = useQueryParams()

  const { coordinates } = useGeolocation()

  const { fullName, file, bio, id, meta, lastParentCategories, status } =
    artist.allArtists.edges[0].node

  const isStarPrivate = PRIVATE_STATUS_LIST.includes(status)
  const artistMeta = useParseJSON<ArtistMeta>(meta)

  const hideEvents = query.defaultCity

  const dateRange =
    !query.dateRange || query.dateRange === DateRangeFilterValues.all
      ? datesRange
      : getEventsDateRangeFilters(query.dateRange)

  const variables = {
    ...dateRange,
    starSlug: slug,
    eventStartDateGte: isStarPrivate ? null : eventStartDateGte,
    announceDateGte: isStarPrivate ? eventStartDateGte : null,
    statusIn: isStarPrivate ? 'private,public' : 'public',
  }

  const filterData = useCallback(
    (event): boolean => {
      const { eventStartDate } = event
      if (lastParentCategories.includes('Sports')) {
        return differenceInHours(new Date(eventStartDate), getServerDate()) > 24
      }
      return checkEventIsAvailableInVenue(event)
    },
    [lastParentCategories]
  )

  return (
    <>
      <MetaTags title={fullName} description={bio} imageSrc={file} path={STAR(slug)} />
      <Layout.Banner />
      <Layout.HomeHeader fluid />
      <Layout.Main backgroundColor="black">
        <Layout.Main.Sidebar>
          <Sidebar
            spotifyWidgetLink={artistMeta?.spotifyWidgetLink}
            title={fullName}
            image={file}
            info={bio}
            after={
              <Mobile>
                <NumberOfViews artist={{ fullName, slug }} />
              </Mobile>
            }
          >
            <FollowArtist id={id} slug={slug} starName={fullName} />
            <Desktop>
              <NumberOfViews artist={{ fullName, slug }} />
            </Desktop>
          </Sidebar>
        </Layout.Main.Sidebar>
        <Layout.Main.Content>
          <Layout.Main.Content.Widget withPadding>
            <div style={{ display: 'grid', gap: '1.5rem', paddingTop: '1.5rem' }}>
              <AuctionsBanner auctions={auctions} />
              {!!events.allArtworks.edges.length && (
                <NearEventsList
                  variables={variables}
                  location={coordinates}
                  hideTevoLink={artistMeta?.hideTEvoTickets}
                  options={{ lastParentCategories }}
                  filterData={({ eventStartDate }) =>
                    lastParentCategories.includes('Sports')
                      ? differenceInHours(new Date(eventStartDate), getServerDate()) > 24
                      : true
                  }
                />
              )}

              {!hideEvents && (
                <EventsList
                  hideTevoLink={artistMeta?.hideTEvoTickets}
                  title={
                    <Grid
                      gridAutoFlow="column"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={18}
                    >
                      <Heading Tag="h2" size="h4" fontWeight={500}>
                        Events
                      </Heading>
                      <EventsDateRangeFilter />
                    </Grid>
                  }
                  queryOptions={{ variables: { ...variables, first: 10 } }}
                  itemListProps={{
                    noDataText: (
                      <Grid gap={8}>
                        <Typography.Text color="g-gray-1" center>
                          There are currently no events on sale for {fullName}
                        </Typography.Text>
                        <NeonBox title="Get notified when new events are added" maxWidth="520px">
                          <FollowArtist id={id} slug={slug} />
                        </NeonBox>
                      </Grid>
                    ),
                  }}
                  options={{ lastParentCategories, venueName: true }}
                  filterData={filterData}
                />
              )}
            </div>
          </Layout.Main.Content.Widget>
        </Layout.Main.Content>
      </Layout.Main>

      <Layout.Footer />
    </>
  )
}

export default StarPage
